import { useEffect, useRef, useState } from "react";

export type UseMinLoadingOptions = {
  minTimeMs?: number;
  loadingStates?: boolean[];
  startImmediately?: boolean;
  requireAll?: boolean;
};

/**
 * Keeps a loading flag on for a *minimum total* time so it never flashes.
 *
 * The indicator shows while the underlying work runs and, if that work finished
 * faster than `minTimeMs`, stays on until `minTimeMs` has elapsed *since it
 * first appeared*. A load that already took longer than `minTimeMs` hides
 * immediately — no extra tail is added.
 */
export function useMinLoading({
  minTimeMs = 700,
  loadingStates = [],
  startImmediately = false,
  requireAll = false,
}: UseMinLoadingOptions = {}): boolean {
  const isExternallyLoading = requireAll
    ? loadingStates.every(Boolean)
    : loadingStates.some(Boolean);

  const [shown, setShown] = useState(startImmediately || isExternallyLoading);
  const shownAtRef = useRef(
    startImmediately || isExternallyLoading ? Date.now() : 0,
  );

  useEffect(() => {
    if (isExternallyLoading) {
      if (!shown) shownAtRef.current = Date.now();
      setShown(true);
      return;
    }
    if (!shown) return;
    const elapsed = Date.now() - shownAtRef.current;
    if (elapsed >= minTimeMs) {
      setShown(false);
      return;
    }
    const id = setTimeout(() => setShown(false), minTimeMs - elapsed);
    return () => clearTimeout(id);
  }, [isExternallyLoading, shown, minTimeMs]);

  return isExternallyLoading || shown;
}
